import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, RefreshCw, CheckCircle2, AlertCircle, LogIn } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { getHomeRouteByRole } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState("checking"); // 'checking' | 'ok' | 'fail'
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      if (user.must_change_password) {
        navigate("/trocar-senha", { replace: true });
        return;
      }
      const target = location.state?.from || getHomeRouteByRole(user.role);
      navigate(target, { replace: true });
    }
  }, [user, navigate, location]);

  // Ping backend on mount to confirm connectivity
  useEffect(() => {
    import("@/lib/api").then(({ api }) => {
      api.get("/health").then(() => setApiStatus("ok")).catch(() => setApiStatus("fail"));
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();
    if (!cleanEmail || !cleanPassword) {
      toast.error("Informe e-mail e senha.");
      return;
    }
    setLoading(true);
    try {
      const u = await login(cleanEmail, cleanPassword);
      if (u.must_change_password) {
        toast.warning("Defina uma nova senha para continuar.", {
          description: "Por segurança, é obrigatório trocar a senha no primeiro acesso.",
        });
        navigate("/trocar-senha", { replace: true });
      } else {
        toast.success(`Bem-vindo(a), ${u.name}`);
        navigate(getHomeRouteByRole(u.role), { replace: true });
      }
    } catch (err) {
      let title = "Não foi possível entrar.";
      let description = "";
      if (err.response) {
        title = err.response.data?.detail || `Erro ${err.response.status}`;
        description = "Verifique suas credenciais e tente novamente.";
      } else if (err.request) {
        title = "Sem resposta do servidor.";
        description = "Verifique sua conexão. Se persistir, clique em \"Limpar e recarregar\".";
      } else {
        title = err.message || "Erro inesperado.";
      }
      toast.error(title, { description, duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const forceReset = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {}
    window.location.href = `${window.location.origin}/login?reset=${Date.now()}`;
  };

  return (
    <div className="min-h-screen flex bg-paper">
      {/* Left brand panel with Brasília background */}
      <div className="hidden lg:flex w-1/2 bg-ink relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage:
              "url('https://upload.wikimedia.org/wikipedia/commons/4/42/Congresso_Nacional_-_Bras%C3%ADlia.jpg')",
            filter: "saturate(0.7) contrast(1.1)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(11,31,58,0.85) 0%, rgba(11,31,58,0.65) 50%, rgba(11,31,58,0.95) 100%)",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <Logo height={96} bare />
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">
              Gestão transparente do seu contrato de consultoria
            </div>
            <h1 className="serif text-4xl xl:text-5xl font-semibold leading-tight">
              Bem-vindo à<br />sua área<br />exclusiva.
            </h1>
            <p className="mt-6 text-slate-200 leading-relaxed">
              Acesse reuniões, documentos aprovados e o progresso dos trabalhos
              realizados pela CEAP Brasil &amp; Lopes.
            </p>
            <div className="mt-8 pt-6 border-t border-white/15">
              <p className="serif text-lg italic text-gold leading-snug">
                &ldquo;Do centro das decisões políticas em Brasília para o futuro do seu Município.&rdquo;
              </p>
            </div>
          </div>
          <div className="text-xs text-slate-400">
            CEAP Brasil &amp; Lopes &middot; &copy; {new Date().getFullYear()}
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-8 py-12 overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo height={60} />
          </div>

          {/* Server status pill + force reset */}
          <div className="mb-6 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              {apiStatus === "ok" && (
                <span className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                  <CheckCircle2 className="w-3 h-3" /> Servidor conectado
                </span>
              )}
              {apiStatus === "checking" && (
                <span className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                  Verificando servidor...
                </span>
              )}
              {apiStatus === "fail" && (
                <span className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 text-red-800 border border-red-200">
                  <AlertCircle className="w-3 h-3" /> Sem conexão com servidor
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={forceReset}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="force-reset-button"
              title="Limpa cache local e recarrega a página"
            >
              <RefreshCw className="w-3 h-3" /> Limpar e recarregar
            </button>
          </div>

          <div className="mb-6">
            <h2 className="serif text-2xl font-semibold text-slate-900">Acesso à plataforma</h2>
            <p className="text-sm text-slate-600 mt-1">
              Entre com seu e-mail institucional e senha.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-medium text-slate-700">
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu.email@exemplo.com.br"
                autoComplete="email"
                required
                className="mt-1 h-11"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-medium text-slate-700">
                Senha
              </Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha"
                  autoComplete="current-password"
                  required
                  className="h-11 pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-midnight hover:bg-ink text-white font-medium"
              data-testid="login-submit-button"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <p className="mt-6 text-xs text-slate-500 text-center">
            Esqueceu sua senha? Solicite à Diretora a redefinição em <strong>Usuários</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}
